import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TeamPermissions from './TeamPermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TeamMemberManagement = ({ onTeamMemberAdded }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [activeTab, setActiveTab] = useState('members'); // 'members' or 'permissions'
  const [formData, setFormData] = useState({
    name: '',
    role: 'staff',
    email: '',
    phone: '',
    permissions: []
  });

  const availablePermissions = [
    { id: 'newsletter', label: 'Newsletter', icon: '📰', description: 'Access newsletter tab (view/compose)' },
    { id: 'hoa_track', label: 'HOA Track', icon: '📋', description: 'Submit HOA Track requests' },
    { id: 'documents', label: 'Documents', icon: '📄', description: 'View documents tab' },
    { id: 'amenity_management', label: 'Amenity Management', icon: '🏊', description: 'View/manage amenity reservations' },
    { id: 'polls_access', label: 'Polls Access', icon: '🗳️', description: 'View and vote on polls only' },
    { id: 'user_management', label: 'User Management', icon: '👥', description: 'Access user management tab' },
    { id: 'financial_reports', label: 'Financial Reports', icon: '💰', description: 'View budget/financial tab' },
    { id: 'calendar_management', label: 'Calendar', icon: '📅', description: 'View community calendar' }
  ];

  const roleOptions = [
    { value: 'attorney', label: 'Attorney', icon: '⚖️' },
    { value: 'accountant', label: 'Accountant', icon: '💼' },
    { value: 'staff', label: 'Staff', icon: '👔' },
    { value: 'other', label: 'Other', icon: '👤' }
  ];

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hoa_token');
      const response = await axios.get(`${BACKEND_URL}/api/team-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data.team_members || []);
    } catch (error) {
      console.error('Error loading team members:', error);
      alert('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        role: member.role,
        email: member.email,
        phone: member.phone || '',
        permissions: member.permissions || []
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        role: 'staff',
        email: '',
        phone: '',
        permissions: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setFormData({
      name: '',
      role: 'staff',
      email: '',
      phone: '',
      permissions: []
    });
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('hoa_token');
      
      if (editingMember) {
        // Update existing member
        await axios.put(
          `${BACKEND_URL}/api/team-members/${editingMember.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Team member updated successfully!');
      } else {
        // Create new member
        await axios.post(
          `${BACKEND_URL}/api/team-members`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Team member added successfully! It will appear in Pending Approvals.');
      }
      
      handleCloseModal();
      loadTeamMembers();
      
      // Trigger parent component to refresh pending approvals
      if (onTeamMemberAdded) {
        onTeamMemberAdded();
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Failed to save team member: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('hoa_token');
      await axios.delete(`${BACKEND_URL}/api/team-members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Team member removed successfully!');
      loadTeamMembers();
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Failed to delete team member');
    }
  };

  const handleResetPassword = async (memberId, memberName, memberEmail) => {
    if (!window.confirm(`Send temporary password to ${memberName} (${memberEmail})?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('hoa_token');
      await axios.post(
        `${BACKEND_URL}/api/team-members/${memberId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`✅ Temporary password sent to ${memberEmail}. The team member will need to change it on first login.`);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getRoleIcon = (role) => {
    const roleOption = roleOptions.find(r => r.value === role);
    return roleOption ? roleOption.icon : '👤';
  };

  const getRoleLabel = (role) => {
    const roleOption = roleOptions.find(r => r.value === role);
    return roleOption ? roleOption.label : role;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-500">Loading team members...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Team Member Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage team members and their granular permissions
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('members')}
            className={`${
              activeTab === 'members'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <span>👥</span>
            <span>Team Members</span>
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`${
              activeTab === 'permissions'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <span>🔐</span>
            <span>Permissions</span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'members' && (
        <div className="space-y-4">
          {/* Add Button */}
          <div className="flex justify-end">
            <button
              onClick={() => handleOpenModal()}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
            >
              <span>➕</span>
              <span>Add Team Member</span>
            </button>
          </div>

          {/* Team Members List */}
          {teamMembers.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="text-gray-400 text-4xl mb-3">👥</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
              <p className="text-gray-600 mb-4">
                Add attorneys, accountants, or staff members to help manage your HOA
              </p>
              <button
                onClick={() => handleOpenModal()}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center space-x-2"
              >
                <span>➕</span>
                <span>Add Your First Team Member</span>
              </button>
            </div>
          ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{getRoleIcon(member.role)}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{member.name}</h3>
                    <p className="text-xs text-gray-500">{getRoleLabel(member.role)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="mr-2">📧</span>
                  <span className="truncate">{member.email}</span>
                </div>
                {member.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">📞</span>
                    <span>{member.phone}</span>
                  </div>
                )}
              </div>

              {/* Permissions */}
              {member.permissions && member.permissions.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Access:</p>
                  <div className="flex flex-wrap gap-1">
                    {member.permissions.map((perm) => {
                      const permission = availablePermissions.find(p => p.id === perm);
                      return permission ? (
                        <span
                          key={perm}
                          className="bg-indigo-100 text-indigo-700 text-xs px-2 py-1 rounded-full"
                        >
                          {permission.icon} {permission.label}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => handleOpenModal(member)}
                  className="bg-gray-100 text-gray-700 px-2 py-1.5 rounded text-xs hover:bg-gray-200 transition-colors"
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleResetPassword(member.id, member.name, member.email)}
                  className="bg-blue-100 text-blue-700 px-2 py-1.5 rounded text-xs hover:bg-blue-200 transition-colors"
                >
                  🔐 Reset
                </button>
                <button
                  onClick={() => handleDelete(member.id, member.name)}
                  className="bg-red-100 text-red-700 px-2 py-1.5 rounded text-xs hover:bg-red-200 transition-colors"
                >
                  🗑️ Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <h3 className="text-xl font-bold text-gray-900">
                {editingMember ? 'Edit Team Member' : 'Add Team Member'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="John Doe"
                  required
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.icon} {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="john.doe@example.com"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="(555) 123-4567"
                />
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Permissions
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select which areas this team member can access
                </p>
                <div className="space-y-2">
                  {availablePermissions.map((permission) => (
                    <label
                      key={permission.id}
                      className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(permission.id)}
                        onChange={() => handlePermissionToggle(permission.id)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-700">
                        {permission.icon} {permission.label}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 No permissions selected means no access to the portal
                </p>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {editingMember ? 'Update' : 'Add'} Team Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <TeamPermissions 
          hoaId={localStorage.getItem('hoa_id')} 
          api={axios}
        />
      )}
    </div>
  );
};

export default TeamMemberManagement;
